import {
  DatePicker,
  Form,
  FormItem,
  NavigationModal,
  TextArea
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { DeathDetails } from 'types/deathDetails'
import RotaWarningBox from './components/RotaWarningBox'

type DeathDetailsModalProps = {
  showModal: boolean
  onCancel: () => void
  onConfirm: (deathDetails: DeathDetails) => void
  backNavigation: () => void
  name: string
  initialValues?: DeathDetails
  isLoading?: boolean
  showRotaWarning?: boolean
}

type DeathDetailsFormValues = {
  deathDetails?: string
  deathDate?: Dayjs
}

const DeathDetailsModal = ({
  showModal,
  onCancel,
  onConfirm,
  backNavigation,
  name,
  initialValues,
  isLoading,
  showRotaWarning = false
}: DeathDetailsModalProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm<DeathDetailsFormValues>()

  const handleConfirm = async () => {
    let values: DeathDetailsFormValues
    try {
      values = await form.validateFields()
    } catch {
      // validation errors are rendered against the fields themselves
      return
    }

    const deathDetails: DeathDetails = {
      deathDetails: values.deathDetails || null,
      deathDate: values.deathDate?.format('YYYY-MM-DD') || null
    }
    onConfirm(deathDetails)
  }

  const getInitialValues = (): DeathDetailsFormValues => {
    return {
      deathDetails: initialValues?.deathDetails || undefined,
      deathDate: initialValues?.deathDate
        ? dayjs(initialValues.deathDate)
        : undefined
    }
  }

  return (
    <NavigationModal
      modalTitle={t(supConstants.DEATH_DETAILS_TITLE)}
      modalConfirmText={t(supConstants.SAVE)}
      modalCancelText={t(supConstants.CANCEL)}
      showPrompt={showModal}
      confirmNavigation={handleConfirm}
      cancelNavigation={onCancel}
      confirmButtonProps={{ variant: 'primary', isLoading }}
      hasBackButton
      backButtonText={t(supConstants.BACK)}
      backNavigation={backNavigation}
    >
      <Container>
        <SubtitleText>
          {t(supConstants.DEATH_DETAILS_WELLWISH_MESSAGE)}
        </SubtitleText>
        <Form form={form} layout="vertical" initialValues={getInitialValues()}>
          <FormItem
            label={<Text>{t(supConstants.DETAILS_OF_DEATH)}</Text>}
            name="deathDetails"
          >
            <TextArea
              placeholder={t(supConstants.DETAILS_OF_DEATH)}
              rows={3}
              data-testid="death-details-textarea"
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.DATE_OF_DEATH)}</Text>}
            name="deathDate"
          >
            <StyledDatePicker
              format="DD-MM-YYYY"
              data-testid="death-details-date-picker"
            />
          </FormItem>
        </Form>
        {showRotaWarning && (
          <RotaWarningBox
            name={name}
            status="deceased"
            confirmButtonText={t(supConstants.SAVE)}
          />
        )}
      </Container>
    </NavigationModal>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 300px;
`

const SubtitleText = styled(Text)`
  font-style: italic;
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

export default DeathDetailsModal
